import * as React from 'react'
import { useRouter } from 'next/router'

import { hooks } from '@bob/design-system'

import * as searchUtil from './search'
import { sort, Sort, SortType } from './sort'
import { usePagination, PaginationInfo, Pagination } from './usePagination'
import { useQuery, Query } from './useQuery'
import { useSearch, Search } from './useSearch'

export type SearchEngine<ITEM> = {
  ready: boolean
  result: {
    status: 'success' | 'request'
    total: number
    page: ITEM[]
    facets: searchUtil.SearchResult<ITEM>['facets']
  }
  search: Search<ITEM>['search']
  sort: {
    configSort: Sort<ITEM>[]
    sort: (sortType: SortType) => void
  }
  query: Omit<Query, 'add' | 'remove' | 'reset' | 'text'> & {
    add: (index: string, bucket: string) => void
    remove: (index: string, bucket: string) => void
    reset: () => void
    text: (text: string) => void
  }
  pagination: Omit<Pagination, 'paginate' | 'setSize'> & {
    pageHref: (page: number) => string
    setSize: (size: number) => void
  }
}

type IURLSearchParams = {
  filters: { [s: string]: string[] }
  page?: number
  size?: number
  sort: string
}

export function useSearchEngine<ITEM>(
  items: ITEM[],
  config: searchUtil.SearchConfig<ITEM> & {
    defaultSize: number
    defaultPage: number
    defaultQuery: searchUtil.SearchQuery
    sorts: Sort<ITEM>[]
  },
): SearchEngine<ITEM> {
  const router = useRouter()
  const search = useSearch(items, config)

  const query = useQuery(config.defaultQuery)
  const pagination = usePagination({ page: 1, size: Infinity }, search.result.list)

  const searchRef = React.useRef(search)
  searchRef.current = search

  const paginationRef = React.useRef(pagination)
  paginationRef.current = pagination

  const queryRef = React.useRef(query)
  queryRef.current = query

  const [isReady, setIsReady] = React.useState(false)

  React.useEffect(() => {
    setUrlParams(window.location.search)
    router.events.on('routeChangeComplete', onRouteChangeComplete)

    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete)
    }

    function onRouteChangeComplete(targetUrl: string): void {
      setUrlParams(window.location.search)
      const url = new URL(window.location.origin + targetUrl)
      const parsed = parse(url.search)

      paginationRef.current.paginate(parsed['page'] || config.defaultPage)
    }
  }, [])

  React.useEffect(() => {
    if (!isReady) {
      if (search.status !== 'request') {
        setIsReady(true)
      }
    }
  }, [search])

  const setQueryDebounced = hooks.useDebounce((nextQuery: searchUtil.SearchQuery) => {
    setQuery(nextQuery)

    const url = new URL(window.location.href)
    url.search = stringify(
      nextQuery.text === ''
        ? {
            filters: nextQuery.filters,
            sort: nextQuery.sortType !== config.defaultQuery.sortType ? SortType[nextQuery.sortType] : undefined,
          }
        : {
            searchableTag: nextQuery.text,
            filters: nextQuery.filters,
            sort: nextQuery.sortType !== config.defaultQuery.sortType ? SortType[nextQuery.sortType] : undefined,
          },
    )

    router.push(url, undefined, { shallow: true })
  }, false)

  const resultSort = config.sorts.find((sort) => sort.type === query.value.sortType)
  const sortedItems = resultSort ? sort(search.result.list, resultSort) : search.result.list

  return {
    ready: isReady,
    result: {
      status: search.status,
      total: search.result.list.length,
      page: sortedItems.slice((pagination.page - 1) * pagination.size, pagination.page * pagination.size),
      facets: search.result.facets,
    },
    search: search.search,
    sort: {
      configSort: config.sorts,
      sort: sortItems,
    },
    query: {
      ...query,
      add,
      remove,
      reset,
      text,
    },
    pagination: {
      ...pagination,
      pageHref,
      setSize,
    },
  }

  function add(index: string, bucket: string): void {
    const nextQuery = query.add(index, bucket)
    onQueryChange(false, nextQuery)
  }

  function remove(index: string, bucket: string): void {
    const nextQuery = query.remove(index, bucket)
    onQueryChange(false, nextQuery)
  }

  function reset(): void {
    const nextQuery = query.reset()
    onQueryChange(false, nextQuery)
  }

  function sortItems(sortType: SortType): void {
    const nextQuery = query.sort(sortType)
    onQueryChange(false, nextQuery)
  }

  function text(text: string): void {
    const nextQuery = query.text(text)
    setQueryDebounced(nextQuery)
  }

  function pageHref(page: number): string {
    const url = getPageUrl({ ...pagination, page })
    return url.toString().replace(url.origin, '')
  }

  function setSize(size: number): void {
    const nexPagination = pagination.setSize(size)
    onPaginationChange(nexPagination)
  }

  function onQueryChange(initial: boolean, query: searchUtil.SearchQuery): void {
    setQuery(query)
    const url = new URL(window.location.href)

    const nextValue: {
      filters: { [s: string]: string[] }
      searchableTag?: string
      signatory?: string
      sort: string | undefined
    } = {
      filters: query.filters,
      sort: query.sortType !== config.defaultQuery.sortType ? SortType[query.sortType] : undefined,
    }
    if (query.text !== '') {
      nextValue.searchableTag = query.text
    }

    url.search = stringify(nextValue)

    if (initial) {
      router.replace(url, undefined, { shallow: true })
    } else {
      router.push(url, undefined, { shallow: true })
    }
  }

  function setQuery(query: searchUtil.SearchQuery): void {
    search.search(query)
    pagination.paginate(1)
  }

  function getPageUrl(pagination: PaginationInfo): URL {
    const url = new URL(window.location.href)
    const params: IURLSearchParams = {
      filters: query.value.filters,
      sort: SortType[query.value.sortType],
    }

    if (pagination.size !== config.defaultSize) {
      params.size = pagination.size
    }

    if (pagination.page !== 1) {
      params.page = pagination.page
    }
    url.search = stringify(params)
    return url
  }

  function onPaginationChange(pagination: PaginationInfo): void {
    router.push(getPageUrl(pagination), undefined, { shallow: true })
  }

  function setUrlParams(searchString: string): void {
    const parsed = parse(searchString)
    paginationRef.current.set(parsed['page'] || config.defaultPage, parsed['size'] || config.defaultSize)

    const defaultSortType = config.defaultQuery.sortType
    let sortType: SortType | string = defaultSortType
    if (parsed.sort !== undefined) {
      // On vérifie si la string passé dans l'url correspond avec un
      // type de sort existant sinon on revient a celui par default
      sortType = SortType[parsed.sort as any] !== undefined ? SortType[parsed.sort as any] : sortType
    }

    // Si jamais on passe dans l'url un number au lieu d'une string
    if (typeof sortType === 'string') {
      sortType = defaultSortType
    }

    // Hack du type parce qu'il veut que parsed.sort soit un number / any
    // mais alors la variable sortType devient de type string alors que c'est bien un SortType
    // d'ou le force avec as pour le remettre du bon type
    const query = {
      text: parsed.searchableTag ?? '',
      filters: parsed.filters,
      sortType: sortType as SortType,
    }
    queryRef.current.set(query)
    searchRef.current.search(query)
  }
}

type Parsed = {
  gclid?: string
  page?: number
  size?: number
  sort?: string
  filters: {
    [s: string]: string[]
  }
  searchableTag?: string
  utm_campaign?: string
  utm_medium?: string
  utm_source?: string
}

function parse(searchString: string): Parsed {
  const search = new URLSearchParams(searchString)
  const parsed: Parsed = {
    filters: {},
  }
  const gclid = search.get('gclid')
  parsed['gclid'] = gclid === null ? undefined : String(gclid)
  search.delete('gclid')

  const searchableTag = search.get('searchableTag')
  parsed['searchableTag'] = searchableTag === null ? undefined : String(searchableTag)
  search.delete('searchableTag')

  const page = search.get('page')
  parsed['page'] = page === null ? undefined : Number(page)
  search.delete('page')

  const size = search.get('size')
  parsed['size'] = size === null ? undefined : Number(size)
  search.delete('size')

  const sort = search.get('sort')
  parsed['sort'] = sort === null ? undefined : String(sort)
  search.delete('sort')

  const utmCampaign = search.get('utm_campaign')
  parsed['utm_campaign'] = utmCampaign === null ? undefined : String(utmCampaign)
  search.delete('utm_campaign')

  const utmSource = search.get('utm_source')
  parsed['utm_source'] = utmSource === null ? undefined : String(utmSource)
  search.delete('utm_source')

  const utmMedium = search.get('utm_medium')
  parsed['utm_medium'] = utmMedium === null ? undefined : String(utmMedium)
  search.delete('utm_medium')

  for (const [key, value] of search.entries()) {
    parsed.filters[key] = value.split(',')
  }

  return parsed as any
}

function stringify(parsed: Parsed): string {
  const search = new URLSearchParams()
  if (parsed.page !== undefined) {
    search.set('page', String(parsed.page))
  }
  if (parsed.size !== undefined) {
    search.set('size', String(parsed.size))
  }
  if (parsed.sort !== undefined) {
    search.set('sort', String(parsed.sort))
  }
  if (parsed.searchableTag !== undefined) {
    search.set('searchableTag', String(parsed.searchableTag))
  }

  for (const [key, value] of Object.entries(parsed.filters)) {
    search.set(key, value.join(','))
  }
  search.sort()

  return search.toString()
}
